import { ControlledTabs } from '@components/Form';
import { FormulaEditor } from '@components/FormulaEditor';
import type { ConstructionTypeData, GlobalLimitedCostData } from '@gql/graphql';
import { GridItem, Loader } from '@uikit';

import type React from 'react';
import { useMemo, useRef } from 'react';
import type { Control, FieldValues, UseFormClearErrors, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { getOtherFormulaSourceObjectGql } from '../../../../Common/Contract/contractQueries';
import Styles from '../BimCalculationLimitedEditModal.module.css';
import type { ChapterItem, CreatingTypes, TabItem } from '../commonData';
import { invisibleStyle, TabsIds, tabsItems } from '../commonData';

import { BimCalculationLimitedModalCommonTab } from './BimCalculationLimitedModalCommonTab';
import { BimCalculationLimitedModalPercentTab } from './BimCalculationLimitedModalPercentTab';

interface IBimCalculationLimitedEditForm {
    activeTab: TabItem;
    calculationId?: number;
    selectedChapter: any;
    constructionTypes?: ConstructionTypeData[];
    handleTabChange: ({ value }: { value: TabItem }) => void;
    onSelectedChapterChange: ({ value }: { value: ChapterItem }) => void;
    control: Control;
    creatingType: CreatingTypes;
    isAdditionalTransport?: boolean;
    handleChangeConstructionType: (id: number, state: boolean) => void;
    globalLimitedCost: GlobalLimitedCostData;
    selectedConstructionTypes: Record<string, boolean>;
    setValue: UseFormSetValue<FieldValues>;
    getValues: UseFormGetValues<Record<string, any>>;
    clearErrors: UseFormClearErrors<any>;
    currentConstructionTypeId?: number;
    isDataFetching?: boolean;
    isEditModal?: boolean;
    isCreateModal?: boolean;
    isViewModal?: boolean;
    globalOtherName?: string;
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

const fieldsToCheck = {
    [TabsIds.COMMON]: ['name', 'globalLimitedCostId', 'rationale', 'otherCostType'],
    [TabsIds.PERCENT]: ['cost', 'method', 'percent'],
    [TabsIds.FORMULA]: ['formula'],
};

export const BimCalculationLimitedEditForm: React.FC<IBimCalculationLimitedEditForm> = ({
    activeTab,
    calculationId,
    selectedChapter,
    handleTabChange,
    onSelectedChapterChange,
    control,
    creatingType,
    isAdditionalTransport,
    selectedConstructionTypes,
    currentConstructionTypeId,
    handleChangeConstructionType,
    globalLimitedCost,
    constructionTypes,
    setValue,
    getValues,
    clearErrors,
    isDataFetching = false,
    isCreateModal = false,
    isEditModal = false,
    isViewModal = false,
    globalOtherName,
    files,
    setFiles,
}) => {
    const formRef = useRef<HTMLFormElement>(null);

    const limitsTabStyle = useMemo(
        () => (activeTab.id === TabsIds.PERCENT ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const formulaTabStyle = useMemo(
        () => (activeTab.id === TabsIds.FORMULA ? undefined : invisibleStyle),
        [activeTab.id],
    );

    return (
        <>
            {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}
            <div className={Styles.innerBlockCondition}>
                <ControlledTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    items={tabsItems}
                    view="bordered"
                    size="s"
                    className={Styles.tabs}
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                />
                <form ref={formRef}>
                    <BimCalculationLimitedModalCommonTab
                        activeTab={activeTab}
                        calculationId={calculationId}
                        creatingType={creatingType}
                        isAdditionalTransport={isAdditionalTransport}
                        control={control}
                        globalLimitedCost={globalLimitedCost}
                        selectedChapter={selectedChapter}
                        onSelectedChapterChange={onSelectedChapterChange}
                        isCreateModal={isCreateModal}
                        isEditModal={isEditModal}
                        globalOtherName={globalOtherName}
                        getValues={getValues}
                        isViewModal={isViewModal}
                    />
                    <GridItem style={limitsTabStyle}>
                        <BimCalculationLimitedModalPercentTab
                            control={control}
                            setValue={setValue}
                            getValues={getValues}
                            clearErrors={clearErrors}
                            selectedConstructionTypes={selectedConstructionTypes}
                            changeSelectedConstructionTypeHandler={handleChangeConstructionType}
                            constructionTypes={constructionTypes}
                            currentConstructionTypeId={currentConstructionTypeId}
                            areFieldsDisabled={isViewModal}
                            isTooltipHidden={!!limitsTabStyle}
                            isAdditionalTransport={isAdditionalTransport}
                            isViewModal={isViewModal}
                            files={files}
                            setFiles={setFiles}
                        />
                    </GridItem>
                    <GridItem style={formulaTabStyle}>
                        <FormulaEditor
                            name="formula"
                            control={control}
                            getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                            tooltipTitle="Формула расчета"
                            isRequired
                            rules={{
                                required: 'Обязательное поле',
                            }}
                            isTooltipHidden={!!formulaTabStyle}
                            tooltipTitleClassName={Styles.formulaTitle}
                            iconTitleSize="s"
                            isReadOnly={isViewModal}
                            isAvailableWithEmptyVariables
                            maxIntegerPartLength={12}
                        />
                    </GridItem>
                </form>
            </div>
        </>
    );
};

BimCalculationLimitedEditForm.displayName = 'BimCalculationLimitedEditForm';
